import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { useChangePassword } from '../../../hooks/auth/useProfile';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const changePasswordMutation = useChangePassword();

  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});
      setSuccessMessage('');
      setShowPasswords({
        old: false,
        new: false,
        confirm: false,
      });
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = 'Ancien mot de passe requis';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'Nouveau mot de passe requis';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Au moins 6 caractères';
    }

    if (formData.newPassword && formData.newPassword === formData.oldPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe doit être différent';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmation requise';
    } else if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      });

      setSuccessMessage('Mot de passe modifié avec succès');
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});

      setTimeout(() => {
        setSuccessMessage('');
        onClose();
      }, 2000);
    } catch (error: any) {
      setErrors({
        oldPassword: error.response?.data?.message || 'Erreur lors du changement de mot de passe',
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-mx-4">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
          <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">
            Changer le mot de passe
          </h2>
          <button
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="tw-p-6">
          {/* Message de succès */}
          {successMessage && (
            <div className="tw-p-3 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-md tw-mb-4">
              <p className="tw-text-sm tw-text-green-700">{successMessage}</p>
            </div>
          )}

          <div className="tw-space-y-4">
            {/* Ancien mot de passe */}
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                Ancien mot de passe <span className="tw-text-red-500">*</span>
              </label>
              <div className="tw-relative">
                <input
                  type={showPasswords.old ? 'text' : 'password'}
                  value={formData.oldPassword}
                  onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
                  className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary ${
                    errors.oldPassword ? 'tw-border-red-300' : 'tw-border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, old: !showPasswords.old })}
                  className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                >
                  {showPasswords.old ? <EyeOff className="tw-w-4 tw-h-4" /> : <Eye className="tw-w-4 tw-h-4" />}
                </button>
              </div>
              {errors.oldPassword && (
                <p className="tw-text-xs tw-text-red-600 tw-mt-1">{errors.oldPassword}</p>
              )}
            </div>

            {/* Nouveau mot de passe */}
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                Nouveau mot de passe <span className="tw-text-red-500">*</span>
              </label>
              <div className="tw-relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary ${
                    errors.newPassword ? 'tw-border-red-300' : 'tw-border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                >
                  {showPasswords.new ? <EyeOff className="tw-w-4 tw-h-4" /> : <Eye className="tw-w-4 tw-h-4" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="tw-text-xs tw-text-red-600 tw-mt-1">{errors.newPassword}</p>
              )}
            </div>

            {/* Confirmation */}
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                Confirmer le nouveau mot de passe <span className="tw-text-red-500">*</span>
              </label>
              <div className="tw-relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary ${
                    errors.confirmPassword ? 'tw-border-red-300' : 'tw-border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                >
                  {showPasswords.confirm ? <EyeOff className="tw-w-4 tw-h-4" /> : <Eye className="tw-w-4 tw-h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="tw-text-xs tw-text-red-600 tw-mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-6 tw-pt-6 tw-border-t tw-border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={changePasswordMutation.isLoading}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {changePasswordMutation.isLoading ? 'Modification...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
